import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TranslationKey } from '../../i18n/translationKeys';
import { PageTitle } from '../../ui/PageTitle/PageTitle';
import { isDevToolsEnabled } from '../../env';
import { devRankingService, DEFAULT_SCORER_WEIGHTS, type ScorerWeights } from '../../services/devRankingService';
import { ScorerWeightsPanel } from './ScorerWeightsPanel';
import { getCategoryIcon } from '../../utils/relance/taskCategoryUtils';
import { formatCurrency } from '../../utils/formatters';
import { TaskCategory } from '../../types/enums';
import type { DevRankedClientDto } from './types';

const NormalizedScoreCell: React.FC<{ client: DevRankedClientDto }> = ({ client }) => {
    const { t } = useTranslation();
    const [hovered, setHovered] = useState(false);
    const { breakdown } = client;

    return (
        <div
            className="relative inline-block"
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <span
                className="cursor-help text-xs underline decoration-dotted"
                data-testid="dev-ranking-normalized-score"
            >
                {client.normalizedScore.toFixed(3)}
            </span>
            {hovered && (
                <div className="absolute bottom-full left-1/2 z-10 mb-xs -translate-x-1/2 rounded bg-primary-1000 p-s text-xs text-primary-50 shadow-medium whitespace-nowrap">
                    <div className="font-semibold mb-xs">{t(TranslationKey.DevRanking.Breakdown.Title)}</div>
                    <div>{t(TranslationKey.DevRanking.Breakdown.Delay)}: {breakdown.weightedDelayScore.toFixed(3)}</div>
                    <div>{t(TranslationKey.DevRanking.Breakdown.Amount)}: {breakdown.weightedAmountScore.toFixed(3)}</div>
                    <div>{t(TranslationKey.DevRanking.Breakdown.Complexity)}: {breakdown.weightedComplexityScore.toFixed(3)}</div>
                    <div>{t(TranslationKey.DevRanking.Breakdown.Credit)}: {breakdown.weightedCreditScore.toFixed(3)}</div>
                    <div>{t(TranslationKey.DevRanking.Breakdown.History)}: {breakdown.weightedHistoryScore.toFixed(3)}</div>
                    <div className="mt-xs border-t border-primary-600 pt-xs font-semibold">
                        {t(TranslationKey.DevRanking.Breakdown.Total)}: {breakdown.total.toFixed(3)}
                    </div>
                </div>
            )}
        </div>
    );
};

// The devtools gate lives in its own component, which calls no hooks. Guarding inside the content
// component meant an early return above six hooks — the hook count then differs between renders,
// which is what react-hooks/rules-of-hooks forbids. Moving the guard below the hooks would satisfy
// the rule but start fetching scoring options on a page that only redirects.
export const DevRankingPage: React.FC = () =>
    isDevToolsEnabled ? <DevRankingContent /> : <Navigate to="/not-found" replace />;

const DevRankingContent: React.FC = () => {
    const { t } = useTranslation();
    const [weights, setWeights] = useState<ScorerWeights>(DEFAULT_SCORER_WEIGHTS);
    const [results, setResults] = useState<DevRankedClientDto[] | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        devRankingService.getScoringOptions()
            .then(setWeights)
            .catch(() => {/* silently fall back to defaults */});
    }, []);

    const handleSubmit = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const ranked = await devRankingService.getRankPreview(weights);
            setResults(ranked);
        } catch {
            setError(t(TranslationKey.DevRanking.Error));
        } finally {
            setIsLoading(false);
        }
    };

    const handleReset = () => setWeights(DEFAULT_SCORER_WEIGHTS);

    return (
        <div className="flex flex-col gap-xl p-xl">
            <PageTitle data-testid="dev-ranking-title">{t(TranslationKey.DevRanking.Title)}</PageTitle>

            <ScorerWeightsPanel
                weights={weights}
                onWeightsChange={setWeights}
                onReset={handleReset}
                onSubmit={handleSubmit}
                isLoading={isLoading}
                status={error}
                submitIdleLabel={t(TranslationKey.DevRanking.Calculate)}
                submitRunningLabel={t(TranslationKey.DevRanking.Calculating)}
                testIdPrefix="dev-ranking-weights"
            />

            {(!results || results.length === 0) && !isLoading && (
                <p className="text-s text-primary-600" data-testid="dev-ranking-empty">
                    {t(TranslationKey.DevRanking.Empty)}
                </p>
            )}

            {results && results.length > 0 && (
                <div className="overflow-x-auto" data-testid="dev-ranking-table">
                    <table className="w-full text-s">
                        <thead>
                            <tr className="border-b border-primary-200 text-left text-xs text-primary-500">
                                <th className="pb-s pr-m">{t(TranslationKey.DevRanking.Rank)}</th>
                                <th className="pb-s pr-m"></th>
                                <th className="pb-s pr-m">Name</th>
                                <th className="pb-s pr-m">Description</th>
                                <th className="pb-s pr-m">Amount</th>
                                <th className="pb-s pr-m">{t(TranslationKey.DevRanking.RawScore)}</th>
                                <th className="pb-s">{t(TranslationKey.DevRanking.NormalizedScore)}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {results.map((client, index) => (
                                <tr
                                    key={client.id}
                                    className="border-b border-primary-100 hover:bg-primary-50"
                                    data-testid="dev-ranking-row"
                                >
                                    <td className="py-s pr-m text-primary-500">{index + 1}</td>
                                    <td className="py-s pr-m text-primary-500">
                                        {getCategoryIcon(TaskCategory.CUSTOMER_RELANCE)}
                                    </td>
                                    <td className="py-s pr-m font-normal text-primary-1000">{client.customerName}</td>
                                    <td className="py-s pr-m text-primary-600">
                                        {client.daysLate != null && client.daysLate > 0 && (
                                            <span>{client.daysLate}d</span>
                                        )}
                                        {client.creditLimitReachedPercent != null && (
                                            <span className="ml-xs">{client.creditLimitReachedPercent}%</span>
                                        )}
                                    </td>
                                    <td className="py-s pr-m text-primary-1000">{formatCurrency(client.totalDue)}</td>
                                    <td className="py-s pr-m text-xs text-primary-600">
                                        {client.rawScore.toFixed(3)}
                                    </td>
                                    <td className="py-s">
                                        <NormalizedScoreCell client={client} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};
